import React, { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, ChevronRight, ImageIcon } from 'lucide-react';
import MediaPickerModal from './components/MediaPickerModal';

function buildEmpty(subfields) {
  const obj = {};
  subfields.forEach((sf) => {
    obj[sf.key] = '';
  });
  return obj;
}

function isImageKey(key) {
  return /_url$/i.test(key) || /_img$/i.test(key) || /image$/i.test(key) || /photo$/i.test(key);
}

function SubfieldInput({ subfield, value, onChange }) {
  const cls =
    'w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9962E]/40';

  const showPreview = isImageKey(subfield.key);
  const [imgError, setImgError] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);

  React.useEffect(() => {
    setImgError(false);
  }, [value]);

  if (subfield.type === 'textarea' && !showPreview) {
    return (
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className={`${cls} resize-y`}
      />
    );
  }

  return (
    <div className="space-y-1.5">
      {showPreview ? (
        <div className="flex gap-1.5">
          <input
            type="text"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={cls}
            placeholder="Paste URL or pick from library…"
          />
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            title="Pick image from media library"
            className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs text-slate-600 hover:border-[#C9962E] hover:text-[#C9962E] transition-colors whitespace-nowrap"
          >
            <ImageIcon className="w-3.5 h-3.5" />
            Pick
          </button>
        </div>
      ) : (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
        />
      )}
      {showPreview && value && !imgError && (
        <div className="flex items-center gap-2">
          <img
            src={value}
            alt="preview"
            onError={() => setImgError(true)}
            className="h-12 w-12 object-cover rounded border border-slate-200 bg-slate-100 flex-shrink-0"
          />
          <span className="text-[10px] text-slate-400 truncate max-w-[180px]">{value}</span>
        </div>
      )}
      {showPreview && value && imgError && (
        <p className="text-[10px] text-red-400 italic">Image could not be loaded</p>
      )}
      {pickerOpen && (
        <MediaPickerModal
          onSelect={(url) => { onChange(url); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

export default function RepeaterField({ value = [], onChange, subfields, fieldLabel }) {
  const [collapsed, setCollapsed] = useState({});

  const items = Array.isArray(value) ? value : [];

  const update = (index, subfieldKey, newVal) => {
    const next = items.map((item, i) =>
      i === index ? { ...item, [subfieldKey]: newVal } : item
    );
    onChange(next);
  };

  const addItem = () => {
    onChange([...items, buildEmpty(subfields)]);
    setCollapsed((prev) => ({ ...prev, [items.length]: false }));
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
    setCollapsed((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const ki = Number(k);
        if (ki < index) next[ki] = prev[ki];
        else if (ki > index) next[ki - 1] = prev[ki];
      });
      return next;
    });
  };

  const moveItem = (index, direction) => {
    const next = [...items];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const toggleCollapse = (index) => {
    setCollapsed((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const getItemLabel = (item, index) => {
    const titleKey = subfields.find((sf) => ['title', 'label', 'name', 'value', 'industry', 'step'].includes(sf.key));
    const preview = titleKey ? item[titleKey.key] : null;
    return preview ? `${index + 1}. ${preview}` : `Item ${index + 1}`;
  };

  return (
    <div className="space-y-2">
      {items.length === 0 && (
        <p className="text-xs text-slate-400 italic py-2">No items yet — click Add to get started.</p>
      )}

      {items.map((item, index) => {
        const isCollapsed = collapsed[index] !== false && index < items.length - 1;

        return (
          <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
            <div
              className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 cursor-pointer select-none"
              onClick={() => toggleCollapse(index)}
            >
              <ChevronRight
                className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${!isCollapsed ? 'rotate-90' : ''}`}
              />
              <span className="text-xs font-medium text-slate-600 flex-1 truncate">
                {getItemLabel(item, index)}
              </span>
              <div className="flex items-center gap-0.5 ml-auto" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => moveItem(index, -1)}
                  disabled={index === 0}
                  title="Move up"
                  className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 disabled:opacity-30 transition-colors"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(index, 1)}
                  disabled={index === items.length - 1}
                  title="Move down"
                  className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 disabled:opacity-30 transition-colors"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  title="Delete item"
                  className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {!isCollapsed && (
              <div className="p-3 space-y-2.5 bg-white">
                {subfields.map((sf) => (
                  <div key={sf.key}>
                    <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                      {sf.label}
                      {sf.required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    <SubfieldInput
                      subfield={sf}
                      value={item[sf.key]}
                      onChange={(val) => update(index, sf.key, val)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addItem}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-slate-300 text-xs text-slate-500 hover:border-[#C9962E] hover:text-[#C9962E] transition-colors w-full justify-center"
      >
        <Plus className="w-3.5 h-3.5" />
        Add {fieldLabel || 'Item'}
      </button>
    </div>
  );
}
